import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Mail, Send, CalendarPlus, Copy, CheckCircle2,
  MessageSquareText, Smartphone, Info, ShieldCheck, User, Users, MessageCircle, AlertTriangle,
  ChevronDown, Phone, Printer, X, ShieldAlert,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  getLetter, sendNotifications, letterIcsUrl, getOwnership, generateCallScript, updateLetterStatus, escalateToBackup,
} from "@/lib/api";
import { RetiredNoteReply } from "@/components/RetiredNoteReply";
import {
  googleCalendarUrl, outlookCalendarUrl, yahooCalendarUrl,
} from "@/lib/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const SHARE_TIERS = [
  { value: "action_only", title: "Action Only", desc: "Share just the action and the deadline. Keeps the details private." },
  { value: "summary",     title: "Summary",     desc: "Share the plain-language summary, deadline, action, and amount." },
  { value: "full_support",title: "Full Support",desc: "Share everything, including a viewable copy of the original letter." },
];

function formatDeadline(a) {
  if (a?.deadline_text) return a.deadline_text;
  if (a?.deadline) {
    try {
      return new Date(a.deadline + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch { return a.deadline; }
  }
  return "No deadline found";
}

export default function ActionCard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const nudgeParam = searchParams.get("nudge");
  const ownership = getOwnership(id);
  const ownerToken = ownership?.ownerToken;

  const [letter, setLetter] = useState(null);
  const [err, setErr] = useState(null);

  // Form
  const [seniorFirstName, setSeniorFirstName] = useState("");
  const [seniorEmail, setSeniorEmail] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [familyEmail, setFamilyEmail] = useState("");
  const [familyPhone, setFamilyPhone] = useState("");
  const [shareLevel, setShareLevel] = useState("summary");
  const [sendSeniorReminder, setSendSeniorReminder] = useState(true);
  const [seniorPhone, setSeniorPhone] = useState("");
  const [seniorPreferredComm, setSeniorPreferredComm] = useState("email");
  const [seniorConsentSms, setSeniorConsentSms] = useState(false);
  const [weeklyDigestOptIn, setWeeklyDigestOptIn] = useState(false);
  const [markingDone, setMarkingDone] = useState(false);
  // Care Circle backup contacts (up to 2 additional people beyond the primary).
  // Each: { name, email, phone }
  const [backups, setBackups] = useState([]);
  const [escalating, setEscalating] = useState(false);

  // State
  const [sending, setSending] = useState(false);
  const [notifState, setNotifState] = useState(null); // response from notify
  const [smsPreviewOpen, setSmsPreviewOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmLowConfidence, setConfirmLowConfidence] = useState(false);
  const [callScript, setCallScript] = useState(null);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptOpen, setScriptOpen] = useState(false);

  const doGenerateScript = async () => {
    setScriptLoading(true);
    setScriptOpen(true);
    try {
      const s = await generateCallScript(id, ownerToken);
      setCallScript(s);
    } catch {
      toast.error("We couldn't draft a call script — please try again.");
      setScriptOpen(false);
    } finally { setScriptLoading(false); }
  };

  const copyScript = async () => {
    if (!callScript) return;
    const lines = [
      `LetterLifts — Call Script`,
      ``,
      `Purpose: ${callScript.call_purpose}`,
      `Who to call: ${callScript.who_to_call}`,
      callScript.phone_number ? `Phone: ${callScript.phone_number}` : "",
      callScript.verified_source_note ? `\nIMPORTANT: ${callScript.verified_source_note}` : "",
      ``,
      `Opening statement:`,
      callScript.opening_statement,
      ``,
      `Questions to ask:`,
      ...(callScript.questions_to_ask || []).map((q, i) => `  ${i + 1}. ${q}`),
      ``,
      `Have this ready:`,
      ...(callScript.information_to_have_ready || []).map((x) => `  • ${x}`),
      ``,
      `Write down during the call:`,
      ...(callScript.what_to_write_down || []).map((x) => `  • ${x}`),
      ``,
      `Follow up: ${callScript.follow_up}`,
    ].filter(Boolean).join("\n");
    try { await navigator.clipboard.writeText(lines); toast.success("Script copied to clipboard."); } catch { toast.error("Couldn't copy."); }
  };

  useEffect(() => {
    if (!ownerToken) { setErr("This letter isn't available on this device."); return; }
    let alive = true;
    getLetter(id, ownerToken).then((d) => {
      if (!alive) return;
      setLetter(d);
      setSeniorFirstName(d?.notifications?.senior_first_name || "");
      setSeniorEmail(d?.notifications?.senior_email || "");
      setFamilyName(d?.notifications?.family_name || "");
      setFamilyEmail(d?.notifications?.family_email || "");
      setFamilyPhone(d?.notifications?.family_phone || "");
      setShareLevel(d?.notifications?.share_level || "summary");
      setSeniorPhone(d?.notifications?.senior_phone || "");
      setSeniorPreferredComm(d?.notifications?.senior_preferred_comm || "email");
      setSeniorConsentSms(!!d?.notifications?.senior_consent_sms);
      setWeeklyDigestOptIn(!!d?.notifications?.weekly_digest_opt_in);
    }).catch(() => setErr("We couldn't load this letter."));
    return () => { alive = false; };
  }, [id, ownerToken]);

  const analysis = letter?.analysis;
  const supporterConfirmedAt = notifState?.notifications?.supporter_confirmed_at || letter?.notifications?.supporter_confirmed_at;
  const supporterStatus = notifState?.notifications?.status || letter?.notifications?.status || null;
  const supporterStatusUpdatedAt = notifState?.notifications?.status_updated_at || letter?.notifications?.status_updated_at || null;
  const familyStatus = notifState?.notifications?.family_email_status || letter?.notifications?.family_email_status || "not_sent";
  const seniorStatus = notifState?.notifications?.senior_email_status || letter?.notifications?.senior_email_status || "not_sent";
  const smsPreviewText = notifState?.notifications?.sms_preview_text || letter?.notifications?.sms_preview_text;

  const thankYouMessage = notifState?.notifications?.thank_you_message || letter?.notifications?.thank_you_message;
  const thankYouStatus = notifState?.notifications?.thank_you_status || letter?.notifications?.thank_you_status || "not_sent";
  const familyDisplayName = notifState?.notifications?.family_name || letter?.notifications?.family_name || "Your family member";

  // Poll for supporter_confirmed AND thank_you AND live status updates (accepted/in_progress/completed/needs_help)
  // from the Shared page. Runs whenever we have a letter loaded, even if the senior hasn't emailed the family
  // yet (they may have copy-pasted the share link out-of-band).
  useEffect(() => {
    if (!letter || !ownerToken) return;
    // Stop polling once everything is at rest.
    const terminal = supporterStatus === "completed";
    if (supporterConfirmedAt && thankYouStatus === "sent" && terminal) return;
    const iv = setInterval(async () => {
      try {
        const d = await getLetter(id, ownerToken);
        const nextConfirmed = d?.notifications?.supporter_confirmed_at;
        const nextThankStatus = d?.notifications?.thank_you_status;
        const nextStatus = d?.notifications?.status;
        const statusChanged = nextStatus && nextStatus !== supporterStatus;
        if (nextConfirmed || nextThankStatus === "sent" || statusChanged) {
          setNotifState((s) => ({
            ...(s || {}),
            notifications: { ...(s?.notifications || {}), ...(d.notifications || {}) },
          }));
          if (nextConfirmed && !supporterConfirmedAt) toast.success("Trusted supporter confirmed.");
          if (nextThankStatus === "sent" && thankYouStatus !== "sent") {
            toast.success(`${d.notifications.family_name || "They"} sent you a note.`);
          }
          if (statusChanged) {
            const familyName = d.notifications?.family_name || "They";
            const label = nextStatus === "in_progress" ? "in progress" : nextStatus === "completed" ? "completed" : nextStatus === "needs_help" ? "needs help" : nextStatus;
            toast.info(`${familyName}: ${label}`);
          }
        }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [letter, supporterConfirmedAt, thankYouStatus, supporterStatus, id, ownerToken]);

  const doMarkAsDone = async () => {
    setMarkingDone(true);
    try {
      const r = await updateLetterStatus(id, ownerToken, "completed");
      setNotifState((s) => ({
        ...(s || {}),
        notifications: {
          ...(s?.notifications || {}),
          status: r.status,
          status_updated_at: r.status_updated_at,
        },
      }));
      // Personalized confirmation. Prefer the freshly-typed first name, fall back to backend.
      const name = seniorFirstName?.trim() || r.senior_first_name || "";
      const msg = name
        ? `Great, ${name} — this letter is marked as handled.`
        : "Great — this letter is marked as handled.";
      toast.success(msg, { duration: 5000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark this as done right now.");
    } finally { setMarkingDone(false); }
  };

  const doEscalate = async (slot) => {
    if (escalating) return;
    setEscalating(true);
    try {
      const r = await escalateToBackup(id, ownerToken, slot);
      // Merge the new active state into notifState so the UI refreshes.
      setNotifState((s) => ({
        ...(s || {}),
        notifications: r.notifications,
        share_url: r.share_url,
      }));
      setShareUrl(r.share_url);
      const target = (r.notifications?.care_circle || []).find((m) => m.slot === slot);
      toast.success(`Email sent to ${target?.name || "your backup contact"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't reach that backup right now.");
    } finally { setEscalating(false); }
  };

  // If the senior arrived here from the Care Binder escalation banner (`?nudge=1`),
  // scroll the care-circle-status-card into view so the escalate button is front-and-centre.
  useEffect(() => {
    if (!nudgeParam || !letter) return;
    const t = setTimeout(() => {
      const el = document.querySelector("[data-testid='care-circle-status-card']");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => clearTimeout(t);
  }, [nudgeParam, letter, notifState]);

  const doSend = async () => {
    if (!familyName.trim()) { toast.error("Please add your family member's name."); return; }
    if (!familyEmail.trim()) { toast.error("Please add your family member's email."); return; }
    // Validate any partially-filled backups
    for (let i = 0; i < backups.length; i++) {
      const b = backups[i];
      if ((b.name && !b.email) || (b.email && !b.name)) {
        toast.error(`Backup ${i + 2}: please add both a name and an email, or remove the row.`);
        return;
      }
    }
    setSending(true);
    try {
      const care_circle = [
        { name: familyName, email: familyEmail, phone: familyPhone || null },
        ...backups
          .filter((b) => b.name && b.email)
          .map((b) => ({ name: b.name, email: b.email, phone: b.phone || null })),
      ];
      const r = await sendNotifications(id, ownerToken, {
        senior_first_name: seniorFirstName,
        senior_email: seniorEmail || null,
        senior_phone: seniorPhone || null,
        senior_preferred_comm: seniorPreferredComm,
        senior_consent_sms: seniorConsentSms,
        weekly_digest_opt_in: weeklyDigestOptIn && !!seniorEmail,
        care_circle,
        share_level: shareLevel,
        send_senior_reminder: sendSeniorReminder,
        confirm_low_confidence: confirmLowConfidence,
      });
      setNotifState(r);
      setShareUrl(r.share_url);
      const primaryName = r?.notifications?.family_name || familyName;
      if (r.notifications.family_email_status === "sent") toast.success(`Email sent to ${primaryName}.`);
      else toast.error(`We couldn't send the email to ${primaryName}. See the details below.`);
      if (sendSeniorReminder && seniorEmail) {
        if (r.notifications.senior_email_status === "sent") toast.success("Reminder emailed to you.");
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail && detail.toLowerCase().includes("confident")) {
        toast.error("Please confirm the deadline first — see the yellow banner below.", { duration: 6000 });
      } else {
        toast.error(detail || "Something went wrong while sending.");
      }
    } finally { setSending(false); }
  };

  const doSmsPreview = () => {
    if (!familyPhone) { toast("Add your family member's phone below to see the SMS preview.", { icon: <Info /> }); return; }
    if (!notifState) { toast("Click Notify Family first — that generates the preview text.", { icon: <Info /> }); return; }
    setSmsPreviewOpen(true);
  };

  const copyShare = async () => {
    if (!shareUrl) return;
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast.success("Support link copied."); setTimeout(() => setCopied(false), 2500); } catch { toast.error("Couldn't copy."); }
  };

  if (err) {
    return (
      <>
        <Header />
        <main className="mx-auto max-w-2xl px-6 py-16 text-center" data-testid="action-error">
          <h1 className="font-display text-3xl font-bold">Letter not available on this device</h1>
          <p className="mt-3 text-lg text-slate-700">{err}</p>
          <Link to="/upload" className="mt-8 inline-flex rounded-full bg-moss-700 px-6 py-3 text-bone font-bold">Upload a letter</Link>
        </main>
      </>
    );
  }
  if (!letter) return (<><Header /><main className="mx-auto max-w-2xl px-6 py-24 text-center"><p className="text-xl text-slate-600">Loading…</p></main></>);

  return (
    <>
      <Header />
      <main data-testid="action-card-page" className="mx-auto max-w-5xl px-6 py-10">
        <button onClick={() => nav(`/letter/${id}`)} className="mb-4 inline-flex items-center gap-2 text-base font-semibold text-moss-700 hover:underline" data-testid="action-back">
          <ArrowLeft strokeWidth={2.5} className="h-5 w-5" /> Back to the explanation
        </button>

        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900">Your action card</h1>
        <p className="mt-3 text-xl text-slate-700">Here's what to do, and how to loop someone in — only what you choose.</p>

        {/* Summary card */}
        <section className="mt-8 rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8" data-testid="action-summary-card">
          <div className="grid gap-6 sm:grid-cols-3">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-moss-700">What to do</p>
              <p className="mt-2 text-xl leading-relaxed text-slate-900" data-testid="action-required">{analysis.required_action || "No action found."}</p>
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-moss-700">By when</p>
              <p className="mt-2 text-xl leading-relaxed text-slate-900" data-testid="action-deadline">{formatDeadline(analysis)}</p>
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-moss-700">Amount</p>
              <p className="mt-2 text-xl leading-relaxed text-slate-900" data-testid="action-amount">{analysis.amount || "—"}</p>
            </div>
          </div>
          {analysis.deadline && (
            <div className="mt-6" data-testid="add-to-calendar-menu">
              <p className="text-sm font-semibold text-slate-700 mb-2">Add this to your calendar</p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="inline-flex items-center gap-2 rounded-full border-2 border-moss-700 bg-white px-5 py-3 text-base font-bold text-moss-700 hover:bg-moss-50 min-h-[48px]"
                    data-testid="calendar-menu-trigger"
                  >
                    <CalendarPlus strokeWidth={2.5} className="h-5 w-5" />
                    Add to Calendar
                    <ChevronDown strokeWidth={2.5} className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[280px] p-2">
                  <DropdownMenuItem
                    className="text-base py-3 px-3 cursor-pointer"
                    data-testid="calendar-google"
                    onClick={() => {
                      const url = googleCalendarUrl({
                        analysis,
                        seniorName: seniorFirstName || notifState?.notifications?.senior_first_name,
                        shareUrl,
                        guests: familyEmail ? [familyEmail] : [],
                      });
                      if (url) {
                        window.open(url, "_blank", "noopener,noreferrer");
                        toast.success("Opening Google Calendar in a new tab — click Save when it appears.", { duration: 6000 });
                      }
                    }}
                  >
                    <img src="https://www.gstatic.com/images/branding/product/2x/calendar_48dp.png" alt="" className="h-6 w-6 mr-2" />
                    Google Calendar
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-base py-3 px-3 cursor-pointer"
                    data-testid="calendar-outlook"
                    onClick={() => {
                      const url = outlookCalendarUrl({
                        analysis,
                        seniorName: seniorFirstName || notifState?.notifications?.senior_first_name,
                        shareUrl,
                      });
                      if (url) {
                        window.open(url, "_blank", "noopener,noreferrer");
                        toast.success("Opening Outlook Calendar in a new tab — click Save when it appears. (If you use Outlook desktop, download the .ics file instead.)", { duration: 8000 });
                      }
                    }}
                  >
                    <Mail strokeWidth={2.5} className="h-5 w-5 mr-2 text-blue-700" />
                    Outlook (Web)
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-base py-3 px-3 cursor-pointer"
                    data-testid="calendar-yahoo"
                    onClick={() => {
                      const url = yahooCalendarUrl({
                        analysis,
                        seniorName: seniorFirstName || notifState?.notifications?.senior_first_name,
                        shareUrl,
                      });
                      if (url) {
                        window.open(url, "_blank", "noopener,noreferrer");
                        toast.success("Opening Yahoo Calendar in a new tab.", { duration: 5000 });
                      }
                    }}
                  >
                    <CalendarPlus strokeWidth={2.5} className="h-5 w-5 mr-2 text-purple-700" />
                    Yahoo Calendar
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-base py-3 px-3 cursor-pointer"
                    data-testid="calendar-apple-ics"
                    onClick={() => {
                      // Trigger download via anchor
                      const link = document.createElement("a");
                      link.href = letterIcsUrl(id, ownerToken);
                      link.download = "";
                      link.rel = "noopener";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success("Calendar file downloaded. Look for it in your Downloads folder, then double-click to add to Apple Calendar or Outlook desktop.", { duration: 10000 });
                    }}
                  >
                    <CalendarPlus strokeWidth={2.5} className="h-5 w-5 mr-2 text-slate-700" />
                    Apple Calendar / Download (.ics)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <p className="mt-2 text-xs text-slate-500">
                We'll add reminders 3 days, 1 day, and the morning of the deadline.
                {familyEmail && " Your family member's email will be invited."}
              </p>
            </div>
          )}

          {/* Safe Call Prep — smart call script generator */}
          <div className="mt-6 pt-6 border-t-2 border-slate-100">
            <p className="text-sm font-semibold text-slate-700 mb-2">Preparing to call the sender?</p>
            <button
              onClick={doGenerateScript}
              disabled={scriptLoading}
              className="inline-flex items-center gap-2 rounded-full bg-ember-700 text-bone px-5 py-3 text-base font-bold hover:bg-ember-600 min-h-[48px] disabled:opacity-60"
              data-testid="safe-call-prep-btn"
            >
              <Phone strokeWidth={2.5} className="h-5 w-5" />
              {scriptLoading ? "Preparing…" : "Safe Call Prep"}
            </button>
            <p className="mt-2 text-xs text-slate-500">
              We'll draft a script tailored to this letter — the exact questions to ask, what to have ready, and what to write down. We'll also warn you if the letter looks suspicious.
            </p>
          </div>
        </section>

        {/* Note back from the family member */}
        {thankYouStatus === "sent" && thankYouMessage && (
          <section
            className="mt-8 rounded-3xl border-2 border-ember-600/40 bg-gradient-to-br from-ember-50 via-white to-white p-6 sm:p-8 cn-fade-up"
            data-testid="incoming-thank-you-card"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-ember-700 text-bone">
                <MessageCircle strokeWidth={2.5} className="h-5 w-5" />
              </span>
              <h2 className="font-display text-2xl font-bold text-slate-900">A note from {familyDisplayName}</h2>
            </div>
            <div
              className="mt-5 rounded-2xl bg-white border-2 border-ember-100 p-6 text-xl leading-relaxed text-slate-900 whitespace-pre-wrap"
              data-testid="incoming-thank-you-message"
            >
              {thankYouMessage}
            </div>
            <p className="mt-3 text-sm text-slate-500">
              Received {new Date(notifState?.notifications?.thank_you_sent_at || letter?.notifications?.thank_you_sent_at || Date.now()).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
            </p>
          </section>
        )}

        {/* Contacts */}
        <section className="mt-10 grid gap-6 lg:grid-cols-2">
          {/* Senior */}
          <div className="rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8" data-testid="senior-contact-card">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-moss-700 text-bone">
                <User strokeWidth={2.5} className="h-5 w-5" />
              </span>
              <h2 className="font-display text-2xl font-bold">Your details</h2>
            </div>
            <div className="mt-5 space-y-4">
              <Field label="Your first name">
                <Input value={seniorFirstName} onChange={(e) => setSeniorFirstName(e.target.value)} placeholder="Eleanor" data-testid="senior-first-name-input" className="h-14 text-lg border-2" />
              </Field>
              <Field label="Your email (for the reminder)">
                <Input type="email" value={seniorEmail} onChange={(e) => setSeniorEmail(e.target.value)} placeholder="you@example.com" data-testid="senior-email-input" className="h-14 text-lg border-2" />
              </Field>
              <Field label="Your mobile phone number" hint="Used for future text reminders. Live texting is coming soon.">
                <Input type="tel" value={seniorPhone} onChange={(e) => setSeniorPhone(e.target.value)} placeholder="(555) 123-4567" data-testid="senior-mobile-input" className="h-14 text-lg border-2" />
              </Field>
              <Field label="Preferred way to hear from us">
                <RadioGroup value={seniorPreferredComm} onValueChange={setSeniorPreferredComm} className="grid grid-cols-3 gap-3" data-testid="senior-preferred-comm-group">
                  {[
                    { value: "email", label: "Email" },
                    { value: "text",  label: "Text" },
                    { value: "both",  label: "Both" },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      htmlFor={`comm-${opt.value}`}
                      className={`cursor-pointer rounded-2xl border-2 p-4 text-center transition-colors ${seniorPreferredComm === opt.value ? "border-moss-700 bg-moss-50" : "border-slate-200 bg-white hover:border-moss-700"}`}
                      data-testid={`senior-preferred-comm-${opt.value}`}
                    >
                      <RadioGroupItem id={`comm-${opt.value}`} value={opt.value} className="sr-only" />
                      <span className="font-display text-lg font-bold text-slate-900">{opt.label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </Field>
              <label className="mt-2 flex items-start gap-3 rounded-2xl bg-amber-50 p-4 cursor-pointer border-2 border-amber-200">
                <Checkbox checked={seniorConsentSms} onCheckedChange={(v) => setSeniorConsentSms(Boolean(v))} className="mt-0.5 h-5 w-5 border-2 border-moss-700 data-[state=checked]:bg-moss-700" data-testid="senior-sms-consent-checkbox" />
                <span className="text-base text-slate-800">I agree to receive reminders and updates by text message. Message and data rates may apply. Reply STOP to opt out at any time.</span>
              </label>
              <label className="mt-2 flex items-start gap-3 rounded-2xl bg-moss-50/60 p-4 cursor-pointer">
                <Checkbox checked={sendSeniorReminder} onCheckedChange={(v) => setSendSeniorReminder(Boolean(v))} className="mt-0.5 h-5 w-5 border-2 border-moss-700 data-[state=checked]:bg-moss-700" data-testid="send-senior-reminder-checkbox" />
                <span className="text-base text-slate-800">Also email me a gentle reminder with the summary and deadline.</span>
              </label>
              <label
                className={`mt-2 flex items-start gap-3 rounded-2xl p-4 cursor-pointer ${
                  seniorEmail ? "bg-moss-50/60" : "bg-slate-50 opacity-70"
                }`}
                title={seniorEmail ? "" : "Add your email above to enable the weekly digest."}
              >
                <Checkbox
                  checked={weeklyDigestOptIn}
                  onCheckedChange={(v) => setWeeklyDigestOptIn(Boolean(v))}
                  disabled={!seniorEmail}
                  className="mt-0.5 h-5 w-5 border-2 border-moss-700 data-[state=checked]:bg-moss-700"
                  data-testid="weekly-digest-opt-in-checkbox"
                />
                <span className="text-base text-slate-800">
                  Send me a gentle weekly digest of any unhandled letters. You can unsubscribe from every email.
                </span>
              </label>
            </div>
          </div>

          {/* Care Circle */}
          <div className="rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8" data-testid="family-contact-card">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-moss-700 text-bone">
                <Users strokeWidth={2.5} className="h-5 w-5" />
              </span>
              <div>
                <h2 className="font-display text-2xl font-bold">Your care circle</h2>
                <p className="mt-1 text-sm text-slate-600">Add up to three trusted people. We&apos;ll only reach the first one now — if they don&apos;t reply in 48 hours, you can nudge the next.</p>
              </div>
            </div>
            <div className="mt-5 space-y-4" data-testid="care-circle-primary">
              <p className="text-sm font-semibold uppercase tracking-[0.14em] text-moss-700">Primary contact</p>
              <Field label="Their name">
                <Input value={familyName} onChange={(e) => setFamilyName(e.target.value)} placeholder="Sarah" data-testid="family-name-input" className="h-14 text-lg border-2" />
              </Field>
              <Field label="Their email">
                <Input type="email" value={familyEmail} onChange={(e) => setFamilyEmail(e.target.value)} placeholder="sarah@example.com" data-testid="family-email-input" className="h-14 text-lg border-2" />
              </Field>
              <Field label="Their phone (optional)" hint="Live texting coming in a future update. For now we'll show a preview of the text.">
                <Input type="tel" value={familyPhone} onChange={(e) => setFamilyPhone(e.target.value)} placeholder="(555) 123-4567" data-testid="family-phone-input" className="h-14 text-lg border-2" />
              </Field>
            </div>

            {backups.map((b, i) => (
              <div key={i} className="mt-6 space-y-4 rounded-2xl bg-moss-50/50 p-5 border border-moss-100" data-testid={`care-circle-backup-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold uppercase tracking-[0.14em] text-moss-700">Backup {i + 1}</p>
                  <button
                    type="button"
                    onClick={() => setBackups((prev) => prev.filter((_, idx) => idx !== i))}
                    data-testid={`remove-backup-${i + 1}`}
                    className="text-sm font-semibold text-slate-500 hover:text-rose-700 underline underline-offset-2"
                  >
                    Remove
                  </button>
                </div>
                <Field label="Their name">
                  <Input
                    value={b.name}
                    onChange={(e) => setBackups((prev) => prev.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))}
                    placeholder="Michael"
                    data-testid={`backup-${i + 1}-name-input`}
                    className="h-14 text-lg border-2 bg-white"
                  />
                </Field>
                <Field label="Their email">
                  <Input
                    type="email"
                    value={b.email}
                    onChange={(e) => setBackups((prev) => prev.map((x, idx) => idx === i ? { ...x, email: e.target.value } : x))}
                    placeholder="michael@example.com"
                    data-testid={`backup-${i + 1}-email-input`}
                    className="h-14 text-lg border-2 bg-white"
                  />
                </Field>
                <Field label="Their phone (optional)">
                  <Input
                    type="tel"
                    value={b.phone || ""}
                    onChange={(e) => setBackups((prev) => prev.map((x, idx) => idx === i ? { ...x, phone: e.target.value } : x))}
                    placeholder="(555) 987-6543"
                    data-testid={`backup-${i + 1}-phone-input`}
                    className="h-14 text-lg border-2 bg-white"
                  />
                </Field>
              </div>
            ))}

            {backups.length < 2 && (
              <button
                type="button"
                onClick={() => setBackups((prev) => [...prev, { name: "", email: "", phone: "" }])}
                data-testid="add-backup-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-dashed border-moss-700 px-5 py-3 text-base font-semibold text-moss-700 hover:bg-moss-50"
              >
                + Add a backup contact
              </button>
            )}
          </div>
        </section>

        {/* Share level */}
        <section className="mt-10 rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8" data-testid="share-level-card">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-moss-700 text-bone"><ShieldCheck strokeWidth={2.5} className="h-5 w-5" /></span>
            <h2 className="font-display text-2xl font-bold">What would you like to share?</h2>
          </div>
          <p className="mt-2 text-lg text-slate-700">You choose how much they see. You can change your mind anytime by resending.</p>
          <RadioGroup value={shareLevel} onValueChange={setShareLevel} className="mt-5 grid gap-3 sm:grid-cols-3">
            {SHARE_TIERS.map((t) => (
              <label key={t.value} htmlFor={`tier-${t.value}`} className={`cursor-pointer rounded-2xl border-2 p-5 transition-colors ${shareLevel === t.value ? "border-moss-700 bg-moss-50" : "border-slate-200 bg-white hover:border-moss-700"}`} data-testid={`share-tier-${t.value}`}>
                <div className="flex items-start gap-3">
                  <RadioGroupItem id={`tier-${t.value}`} value={t.value} className="mt-1 h-5 w-5 border-2 border-moss-700 text-moss-700" />
                  <div>
                    <p className="font-display text-xl font-bold text-slate-900">{t.title}</p>
                    <p className="mt-1 text-base text-slate-700">{t.desc}</p>
                  </div>
                </div>
              </label>
            ))}
          </RadioGroup>
        </section>

        {/* AI uncertainty confirmation — only shown when the analysis flagged low confidence */}
        {(analysis?.deadline_confidence === "low" || analysis?.confidence === "low") && (
          <section
            className="mt-8 rounded-3xl border-2 border-amber-500 bg-amber-50 p-5 sm:p-6"
            data-testid="low-confidence-gate"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle strokeWidth={2.5} className="mt-1 h-6 w-6 text-amber-800 flex-shrink-0" />
              <div>
                <h3 className="font-display text-xl font-bold text-amber-900">
                  Please confirm the deadline before sending
                </h3>
                <p className="mt-2 text-base leading-relaxed text-amber-900">
                  The AI wasn't fully confident about the date on this letter. Before we email or remind
                  anyone, please check the letter yourself (or ask someone you trust) to make sure the
                  date <b>{formatDeadline(analysis)}</b> is correct.
                </p>
                <label className="mt-4 flex items-start gap-3 cursor-pointer rounded-xl bg-white/70 p-3 border border-amber-300">
                  <Checkbox
                    checked={confirmLowConfidence}
                    onCheckedChange={(v) => setConfirmLowConfidence(Boolean(v))}
                    className="mt-0.5 h-5 w-5 border-2 border-amber-700 data-[state=checked]:bg-amber-700 data-[state=checked]:text-white"
                    data-testid="confirm-low-confidence-checkbox"
                  />
                  <span className="text-base font-semibold text-amber-900">
                    I've checked the letter and confirmed the deadline is correct.
                  </span>
                </label>
              </div>
            </div>
          </section>
        )}

        {/* Send buttons */}
        <section className="mt-10 flex flex-wrap items-center gap-4">
          <button
            onClick={doSend}
            disabled={sending || ((analysis?.deadline_confidence === "low" || analysis?.confidence === "low") && !confirmLowConfidence)}
            className="inline-flex items-center gap-3 rounded-full bg-moss-700 px-8 py-4 text-lg font-bold text-bone hover:bg-moss-800 min-h-[56px] disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="notify-family-btn"
          >
            <Send strokeWidth={2.5} className="h-5 w-5" />
            {sending ? "Sending…" : "Notify Family Member"}
          </button>
          <button onClick={doSmsPreview} className="inline-flex items-center gap-3 rounded-full border-2 border-moss-700 bg-white px-7 py-4 text-lg font-bold text-moss-700 hover:bg-moss-50 min-h-[56px]" data-testid="sms-preview-btn">
            <MessageSquareText strokeWidth={2.5} className="h-5 w-5" />
            Text message — Preview
          </button>
        </section>

        {/* Where this letter stands — always visible.
            Shows family status (if a supporter confirmed) and a Mark as Done button
            so the senior can close the loop themselves any time. */}
        <section
          className={`mt-10 rounded-3xl border-2 p-6 sm:p-8 ${
            supporterStatus === "completed"
              ? "border-emerald-500 bg-emerald-50/70"
              : "border-moss-700 bg-moss-50/40"
          }`}
          data-testid="live-family-status-card"
        >
          <div className="flex items-start gap-3">
            <span className={`grid h-11 w-11 place-items-center rounded-2xl text-bone flex-shrink-0 ${supporterStatus === "completed" ? "bg-emerald-700" : "bg-moss-700"}`}>
              <CheckCircle2 strokeWidth={2.5} className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-2xl font-bold text-slate-900">
                {supporterStatus === "completed"
                  ? "This letter is handled"
                  : supporterConfirmedAt
                    ? `${familyDisplayName} is on it`
                    : "Where this letter stands"}
              </h2>

              {supporterConfirmedAt && supporterStatus ? (
                <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="supporter-live-status">
                  <span className="text-base font-medium text-slate-700">Latest update:</span>
                  {(() => {
                    const map = {
                      accepted:    { label: "Accepted",     className: "bg-blue-100 text-blue-900 border-blue-300" },
                      in_progress: { label: "In progress",  className: "bg-amber-100 text-amber-900 border-amber-300" },
                      completed:   { label: "Completed",    className: "bg-emerald-100 text-emerald-900 border-emerald-300" },
                      needs_help:  { label: "Needs help",   className: "bg-rose-100 text-rose-900 border-rose-300" },
                    };
                    const m = map[supporterStatus] || { label: supporterStatus, className: "bg-slate-100 text-slate-800 border-slate-300" };
                    return (
                      <span className={`inline-flex items-center rounded-full border-2 px-4 py-1.5 text-base font-bold ${m.className}`}>
                        {m.label}
                      </span>
                    );
                  })()}
                  {supporterStatusUpdatedAt && (
                    <span className="text-sm text-slate-500">
                      updated {new Date(supporterStatusUpdatedAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              ) : supporterConfirmedAt ? (
                <p className="mt-2 text-lg text-slate-700">They&apos;ve said they&apos;ll help. You&apos;ll see updates here as they take action.</p>
              ) : (
                <p className="mt-2 text-lg text-slate-700">Once this letter is taken care of, tap the button to close it out.</p>
              )}

              {/* Mark as Done — always available to the senior, unless already completed */}
              {supporterStatus === "completed" ? (
                <p
                  className="mt-4 text-xl leading-relaxed font-semibold text-emerald-900"
                  data-testid="mark-as-done-confirmation"
                >
                  Great{seniorFirstName?.trim() || notifState?.notifications?.senior_first_name || letter?.notifications?.senior_first_name
                    ? `, ${(seniorFirstName?.trim() || notifState?.notifications?.senior_first_name || letter?.notifications?.senior_first_name)}`
                    : ""} — this letter is marked as handled.
                </p>
              ) : (
                <button
                  onClick={doMarkAsDone}
                  disabled={markingDone}
                  data-testid="mark-as-done-btn"
                  className="mt-5 inline-flex items-center gap-3 rounded-full bg-emerald-700 px-7 py-4 text-lg font-bold text-bone hover:bg-emerald-800 disabled:opacity-60 min-h-[56px]"
                >
                  <CheckCircle2 strokeWidth={2.5} className="h-5 w-5" />
                  {markingDone ? "Marking..." : "Mark as Done"}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Care Circle status — visible whenever we have a care_circle stored (i.e., after notify).
            Shows each person's slot with notify + confirmation state, and a big "Reach out to
            [Next Backup]" button if the active supporter hasn't accepted yet. */}
        {(() => {
          const cc = notifState?.notifications?.care_circle || letter?.notifications?.care_circle || [];
          if (!cc.length || cc.length < 2) return null; // only interesting if backups exist
          const activeSlot = notifState?.notifications?.active_slot || letter?.notifications?.active_slot || 1;
          const active = cc.find((m) => m.slot === activeSlot);
          const nextBackup = cc.find((m) => m.slot === activeSlot + 1);
          const activeConfirmed = Boolean(active?.confirmed_at);
          const activeNotifiedAt = active?.notified_at || active?.email_sent_at;
          const hoursSinceNotified = activeNotifiedAt
            ? Math.max(0, (Date.now() - new Date(activeNotifiedAt).getTime()) / 3_600_000)
            : 0;
          const past48h = hoursSinceNotified >= 48;
          const canEscalate = !activeConfirmed && Boolean(nextBackup);
          return (
            <section
              className="mt-10 rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8"
              data-testid="care-circle-status-card"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-moss-700 text-bone">
                  <Users strokeWidth={2.5} className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="font-display text-2xl font-bold text-slate-900">Your care circle</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    We reach one person at a time. You&apos;re free to nudge the next backup any time.
                  </p>
                </div>
              </div>

              <ol className="mt-5 space-y-3" data-testid="care-circle-slots">
                {cc.map((m) => {
                  const isActive = m.slot === activeSlot;
                  const isNotified = Boolean(m.notified_at || m.email_sent_at);
                  const isConfirmed = Boolean(m.confirmed_at);
                  const isPast = m.slot < activeSlot;
                  let stateLabel = "Backup — waiting";
                  let stateClass = "bg-slate-100 text-slate-700 border-slate-300";
                  if (isConfirmed) { stateLabel = "Accepted"; stateClass = "bg-emerald-100 text-emerald-900 border-emerald-300"; }
                  else if (isActive && isNotified) { stateLabel = "Emailed — waiting"; stateClass = "bg-amber-100 text-amber-900 border-amber-300"; }
                  else if (isPast && isNotified && !isConfirmed) { stateLabel = "No reply — moved on"; stateClass = "bg-slate-100 text-slate-500 border-slate-300"; }
                  return (
                    <li
                      key={m.slot}
                      data-testid={`care-slot-${m.slot}`}
                      className={`flex items-start justify-between gap-4 rounded-2xl border-2 p-4 ${
                        isActive ? "border-moss-700 bg-moss-50/40" : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="font-display text-lg font-bold text-slate-900">
                          {m.slot === 1 ? "Primary" : `Backup ${m.slot - 1}`} · {m.name || "—"}
                        </p>
                        {m.email && <p className="mt-0.5 text-sm text-slate-600 truncate">{m.email}</p>}
                        {isNotified && m.email_sent_at && (
                          <p className="mt-0.5 text-xs text-slate-500">
                            Emailed {new Date(m.email_sent_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                          </p>
                        )}
                      </div>
                      <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-bold flex-shrink-0 ${stateClass}`}>
                        {stateLabel}
                      </span>
                    </li>
                  );
                })}
              </ol>

              {canEscalate && (
                <div className={`mt-6 rounded-2xl p-5 border-2 ${past48h ? "bg-amber-50 border-amber-400" : "bg-slate-50 border-slate-200"}`}
                     data-testid="care-circle-escalate-panel">
                  <p className="text-base text-slate-800">
                    {past48h ? (
                      <>
                        It&apos;s been over 48 hours and <b>{active?.name || "your contact"}</b> hasn&apos;t said they&apos;ll help yet.
                        Reach out to <b>{nextBackup.name}</b> now?
                      </>
                    ) : (
                      <>
                        Haven&apos;t heard back from <b>{active?.name || "your contact"}</b>? You can nudge <b>{nextBackup.name}</b> whenever you&apos;re ready.
                      </>
                    )}
                  </p>
                  <button
                    onClick={() => doEscalate(nextBackup.slot)}
                    disabled={escalating}
                    data-testid={`escalate-btn-${nextBackup.slot}`}
                    className={`mt-4 inline-flex items-center gap-3 rounded-full px-7 py-4 text-lg font-bold text-bone disabled:opacity-60 min-h-[56px] ${
                      past48h ? "bg-amber-700 hover:bg-amber-800" : "bg-moss-700 hover:bg-moss-800"
                    }`}
                  >
                    <Users strokeWidth={2.5} className="h-5 w-5" />
                    {escalating ? "Sending..." : `Reach out to ${nextBackup.name}`}
                  </button>
                </div>
              )}
            </section>
          );
        })()}

        {/* Notes from earlier helpers — preserved from any retired supporter (post-escalation). */}
        {(() => {
          const retiredAll = notifState?.notifications?.retired_supporters || letter?.notifications?.retired_supporters || [];
          const retired = retiredAll
            .map((r, idx) => ({ ...r, __index: idx }))
            .filter((r) => r?.thank_you_message);
          if (!retired.length) return null;
          const applyReplyUpdate = (index, updated) => {
            setNotifState((s) => {
              const cur = s?.notifications?.retired_supporters || retiredAll;
              const next = cur.map((x, i) => i === index ? { ...x, ...updated } : x);
              return { ...(s || {}), notifications: { ...(s?.notifications || letter?.notifications || {}), retired_supporters: next } };
            });
          };
          return (
            <section
              className="mt-10 rounded-3xl border-2 border-moss-200 bg-moss-50/40 p-6 sm:p-8"
              data-testid="retired-supporters-card"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-moss-700 text-bone">
                  <MessageCircle strokeWidth={2.5} className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="font-display text-2xl font-bold text-slate-900">Notes from earlier helpers</h2>
                  <p className="mt-1 text-sm text-slate-600">These messages arrived before you moved on to a backup contact. A quick thank-you goes a long way.</p>
                </div>
              </div>
              <ul className="mt-5 space-y-3">
                {retired.map((r) => (
                  <li
                    key={r.__index}
                    data-testid={`retired-note-${r.__index}`}
                    className="rounded-2xl bg-white p-4 border border-moss-200"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-700">
                        From {r.name || "an earlier helper"}
                        {r.thank_you_sent_at && (
                          <span className="ml-2 text-xs font-normal text-slate-500">
                            · {new Date(r.thank_you_sent_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                          </span>
                        )}
                      </p>
                      {r.reply_sent_at && (
                        <span
                          data-testid={`retired-replied-badge-${r.__index}`}
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 text-xs font-bold flex-shrink-0"
                        >
                          ✓ Replied
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-lg leading-relaxed text-slate-800 whitespace-pre-wrap">
                      {r.thank_you_message}
                    </p>
                    <div className="mt-4">
                      <RetiredNoteReply
                        letterId={id}
                        ownerToken={ownerToken}
                        index={r.__index}
                        retired={r}
                        onReplied={(updated) => applyReplyUpdate(r.__index, updated)}
                        testIdPrefix={`action-reply-${r.__index}`}
                      >
                        <button
                          type="button"
                          data-testid={`retired-reply-btn-${r.__index}`}
                          className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors ${
                            r.reply_sent_at
                              ? "bg-white border-2 border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                              : "bg-moss-700 text-bone hover:bg-moss-800"
                          }`}
                        >
                          <Send strokeWidth={2.5} className="h-4 w-4" />
                          {r.reply_sent_at ? "Send another thank-you" : "Send a quick thank-you"}
                        </button>
                      </RetiredNoteReply>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          );
        })()}

        {/* Statuses — only show once at least one email has actually been sent */}
        {((notifState?.notifications?.family_email_status && notifState.notifications.family_email_status !== "not_sent") ||
          (notifState?.notifications?.senior_email_status && notifState.notifications.senior_email_status !== "not_sent") ||
          (letter?.notifications?.family_email_status && letter.notifications.family_email_status !== "not_sent")) && (
          <section className="mt-10 rounded-3xl border-2 border-slate-200 bg-white p-6 sm:p-8" data-testid="notification-status-card">
            <h2 className="font-display text-2xl font-bold">Delivery status</h2>
            <ul className="mt-4 space-y-3 text-lg">
              <StatusLine label="Family notified by email" status={familyStatus} error={notifState?.notifications?.family_email_error} testId="status-family-email" />
              {sendSeniorReminder && seniorEmail && (
                <StatusLine label="Your reminder email" status={seniorStatus} error={notifState?.notifications?.senior_email_error} testId="status-senior-email" />
              )}
              {familyPhone && (() => {
                const smsStatus = notifState?.notifications?.family_sms_status || letter?.notifications?.family_sms_status;
                const smsError = notifState?.notifications?.family_sms_error || letter?.notifications?.family_sms_error;
                const displayName = notifState?.notifications?.family_name || familyName || "your family member";
                if (smsStatus === "sent") {
                  return (
                    <li className="flex items-start gap-3 text-moss-700 font-semibold" data-testid="status-family-sms">
                      <CheckCircle2 strokeWidth={2.5} className="mt-1 h-5 w-5" />
                      <span>Text message sent to {displayName}</span>
                    </li>
                  );
                }
                if (smsStatus === "failed") {
                  return (
                    <li className="flex items-start gap-3 text-red-800 font-semibold" data-testid="status-family-sms">
                      <Info strokeWidth={2.5} className="mt-1 h-5 w-5" />
                      <span>Text to {displayName} — Failed
                        {smsError && <span className="block text-sm font-normal text-red-700">{smsError}</span>}
                      </span>
                    </li>
                  );
                }
                // "preview_only" or missing → legacy behavior (SMS off / not yet configured)
                return (
                  <li className="flex items-start gap-3" data-testid="status-sms-preview">
                    <MessageSquareText strokeWidth={2.5} className="mt-1 h-5 w-5 text-slate-600" />
                    <span>SMS preview generated for {displayName} <span className="text-slate-500">— text messages are turned off right now.</span></span>
                  </li>
                );
              })()}
              {(() => {
                const s = notifState?.notifications?.senior_sms_status || letter?.notifications?.senior_sms_status;
                if (!s || s === "preview_only") return null;
                if (s === "sent") {
                  return (
                    <li className="flex items-start gap-3 text-moss-700 font-semibold" data-testid="status-senior-sms">
                      <CheckCircle2 strokeWidth={2.5} className="mt-1 h-5 w-5" />
                      <span>Your reminder text — Sent</span>
                    </li>
                  );
                }
                const err = notifState?.notifications?.senior_sms_error || letter?.notifications?.senior_sms_error;
                return (
                  <li className="flex items-start gap-3 text-red-800 font-semibold" data-testid="status-senior-sms">
                    <Info strokeWidth={2.5} className="mt-1 h-5 w-5" />
                    <span>Your reminder text — Failed
                      {err && <span className="block text-sm font-normal text-red-700">{err}</span>}
                    </span>
                  </li>
                );
              })()}
              {supporterConfirmedAt && (
                <li className="flex items-start gap-3 text-moss-700 font-semibold" data-testid="status-supporter-confirmed">
                  <CheckCircle2 strokeWidth={2.5} className="mt-1 h-5 w-5" />
                  Trusted supporter confirmed
                </li>
              )}
            </ul>

            {shareUrl && (
              <div className="mt-6 rounded-2xl border-2 border-slate-200 bg-moss-50/40 p-4">
                <p className="text-sm font-semibold uppercase tracking-wider text-moss-700">Secure support link</p>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <code className="rounded-lg bg-white px-3 py-2 text-sm text-slate-700 border border-slate-200 break-all" data-testid="share-url-display">{shareUrl}</code>
                  <button onClick={copyShare} className="inline-flex items-center gap-2 rounded-full border-2 border-moss-700 bg-white px-4 py-2 text-sm font-bold text-moss-700 hover:bg-moss-50" data-testid="copy-share-url">
                    {copied ? <><CheckCircle2 className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy link</>}
                  </button>
                  <a href={shareUrl} target="_blank" rel="noreferrer" className="text-sm font-bold text-moss-700 underline">Open</a>
                </div>
              </div>
            )}
          </section>
        )}

        {/* SMS preview modal (inline) */}
        {smsPreviewOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-6" onClick={() => setSmsPreviewOpen(false)} data-testid="sms-preview-modal">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl border-2 border-slate-200 bg-white p-6">
              <div className="flex items-center gap-3">
                <Smartphone strokeWidth={2.5} className="h-6 w-6 text-moss-700" />
                <h3 className="font-display text-2xl font-bold">SMS preview</h3>
              </div>
              <p className="mt-1 text-sm text-slate-500">This is exactly what the text would say. It has <b>not</b> been sent.</p>
              <div className="mt-6 rounded-3xl bg-slate-100 p-5">
                {(() => {
                  const displayName = notifState?.notifications?.family_name || familyName;
                  const displayPhone = notifState?.notifications?.family_phone || familyPhone;
                  return (
                    <p className="text-xs uppercase tracking-wider text-slate-500">To {displayPhone} {displayName && <span className="text-slate-700">· {displayName}</span>}</p>
                  );
                })()}
                <div className="mt-3 cn-sms-bubble p-4 text-lg" data-testid="sms-bubble">
                  {smsPreviewText}
                </div>
                <p className="mt-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">SMS preview generated</p>
              </div>
              <p className="mt-4 text-sm text-slate-500">Live texting coming in a future update.</p>
              <div className="mt-6 flex justify-end">
                <button onClick={() => setSmsPreviewOpen(false)} className="rounded-full bg-moss-700 px-5 py-2 font-bold text-bone">Close</button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-14 flex flex-wrap items-center gap-4">
          <Link to="/upload" className="inline-flex items-center gap-2 rounded-full border-2 border-slate-300 bg-white px-6 py-3 text-base font-bold text-slate-700 hover:bg-slate-50">
            Upload another letter <ArrowRight strokeWidth={2.5} className="h-5 w-5" />
          </Link>
        </div>

        {/* Safe Call Prep modal */}
        {scriptOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 overflow-y-auto" data-testid="call-script-modal">
            <div className="w-full max-w-2xl rounded-3xl border-2 border-slate-200 bg-white my-8 max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b-2 border-slate-100 rounded-t-3xl p-5 flex items-center justify-between print:hidden">
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-2xl bg-ember-700 text-bone"><Phone strokeWidth={2.5} className="h-5 w-5" /></span>
                  <h2 className="font-display text-2xl font-bold text-slate-900">Safe Call Prep</h2>
                </div>
                <button onClick={() => setScriptOpen(false)} className="p-2 rounded-full hover:bg-slate-100" data-testid="call-script-close" aria-label="Close">
                  <X strokeWidth={2.5} className="h-6 w-6 text-slate-700" />
                </button>
              </div>

              <div id="call-script-print-area" className="p-6 sm:p-8 space-y-6 text-slate-900 print:text-black print:text-xl print:leading-relaxed">
                {scriptLoading && (
                  <p className="text-lg text-slate-600 text-center py-12">Preparing your script — this takes a few seconds…</p>
                )}
                {!scriptLoading && callScript && (
                  <>
                    {callScript.is_suspicious && (
                      <div className="rounded-2xl border-2 border-red-700 bg-red-50 p-5" data-testid="call-script-scam-warning">
                        <div className="flex items-start gap-3">
                          <ShieldAlert strokeWidth={2.5} className="mt-0.5 h-7 w-7 text-red-800 flex-shrink-0" />
                          <div>
                            <h3 className="font-display text-xl font-bold text-red-900">This letter looks suspicious — please don't call the number on it</h3>
                            <p className="mt-2 text-lg text-red-900">{callScript.verified_source_note}</p>
                            {familyEmail && (
                              <p className="mt-3 text-base text-red-900">
                                We recommend also using the "Notify Family Member" button on this page to send this letter to <b>{familyName || "your trusted person"}</b> for a second opinion before you call anyone.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {callScript.warnings?.length > 0 && !callScript.is_suspicious && (
                      <div className="rounded-2xl border-2 border-amber-500 bg-amber-50 p-4" data-testid="call-script-warnings">
                        <p className="text-base font-semibold text-amber-900 flex items-start gap-2">
                          <AlertTriangle strokeWidth={2.5} className="mt-0.5 h-5 w-5" />
                          Heads-up
                        </p>
                        <ul className="mt-2 space-y-1 text-base text-amber-900">
                          {callScript.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                        </ul>
                      </div>
                    )}

                    <div>
                      <p className="text-sm font-semibold uppercase tracking-wider text-ember-700">Why you're calling</p>
                      <p className="mt-1 text-xl">{callScript.call_purpose}</p>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wider text-ember-700">Who to call</p>
                        <p className="mt-1 text-xl font-semibold">{callScript.who_to_call || "—"}</p>
                      </div>
                      {callScript.phone_number && !callScript.is_suspicious && (
                        <div>
                          <p className="text-sm font-semibold uppercase tracking-wider text-ember-700">Phone number (from letter)</p>
                          <p className="mt-1 text-xl font-mono">{callScript.phone_number}</p>
                        </div>
                      )}
                    </div>

                    {callScript.opening_statement && (
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wider text-ember-700">What to say first</p>
                        <p className="mt-1 text-xl leading-relaxed italic bg-slate-50 border-l-4 border-ember-700 pl-4 py-2">"{callScript.opening_statement}"</p>
                      </div>
                    )}

                    {callScript.questions_to_ask?.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wider text-ember-700">Questions to ask</p>
                        <ol className="mt-2 space-y-2 text-xl">
                          {callScript.questions_to_ask.map((q, i) => (
                            <li key={i} className="flex gap-3"><span className="font-bold text-ember-700 min-w-[24px]">{i + 1}.</span><span>{q}</span></li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {callScript.information_to_have_ready?.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wider text-ember-700">Have this ready before you call</p>
                        <ul className="mt-2 space-y-1 text-lg">
                          {callScript.information_to_have_ready.map((x, i) => <li key={i} className="flex gap-2"><span>☐</span><span>{x}</span></li>)}
                        </ul>
                      </div>
                    )}

                    {callScript.what_to_write_down?.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wider text-ember-700">Write these down during the call</p>
                        <ul className="mt-2 space-y-1 text-lg">
                          {callScript.what_to_write_down.map((x, i) => <li key={i} className="flex gap-2"><span>✎</span><span>{x}</span></li>)}
                        </ul>
                      </div>
                    )}

                    {callScript.follow_up && (
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wider text-ember-700">After the call</p>
                        <p className="mt-1 text-lg">{callScript.follow_up}</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {!scriptLoading && callScript && (
                <div className="sticky bottom-0 bg-white border-t-2 border-slate-100 rounded-b-3xl p-5 flex flex-wrap gap-3 print:hidden">
                  <button onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full bg-moss-700 text-bone px-5 py-3 font-bold hover:bg-moss-800 min-h-[48px]" data-testid="call-script-print">
                    <Printer strokeWidth={2.5} className="h-5 w-5" /> Print for the call
                  </button>
                  <button onClick={copyScript} className="inline-flex items-center gap-2 rounded-full border-2 border-moss-700 bg-white text-moss-700 px-5 py-3 font-bold hover:bg-moss-50 min-h-[48px]" data-testid="call-script-copy">
                    <Copy strokeWidth={2.5} className="h-5 w-5" /> Copy script
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-base font-semibold text-slate-800">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-sm text-slate-500">{hint}</p>}
    </div>
  );
}

function StatusLine({ label, status, error, testId }) {
  const style = status === "sent"
    ? { icon: CheckCircle2, cls: "text-moss-700 font-semibold", suffix: "Sent" }
    : status === "failed"
    ? { icon: Info, cls: "text-red-800 font-semibold", suffix: "Failed" }
    : { icon: Mail, cls: "text-slate-600", suffix: "Not sent" };
  const Icon = style.icon;
  return (
    <li className={`flex items-start gap-3 ${style.cls}`} data-testid={testId}>
      <Icon strokeWidth={2.5} className="mt-1 h-5 w-5" />
      <span>
        {label} — {style.suffix}
        {status === "failed" && error && (
          <span className="block text-sm font-normal text-red-700">{error}</span>
        )}
      </span>
    </li>
  );
}
